/* eslint-disable no-constant-condition */

/* eslint-disable react-hooks/exhaustive-deps */

/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable no-promise-executor-return */

/* eslint-disable no-restricted-syntax */
import { useCallback, useEffect, useRef, useState } from 'react';

import CustomButton from '../../../common/CustomButton/CustomButton';
import CustomForm from '../../../common/CustomForm/CustomForm';
import CustomTextarea from '../../../common/CustomTextarea/CustomTextarea';
import { Icon } from '../../../common/Icon/Icon';
import Markdown from '../../../common/Markdown/Markdown';
import Spinner from '../../../common/Spinner/Spinner';
import { useAlert } from '../../../core/context/alert.context';
import { useScreenSizeContext } from '../../../core/context/screenSize.context';
import { capitalize, isEmpty, isJsonString } from '../../../core/helpers';
import { useFormInput } from '../../../core/hooks/useFormInput';
import { ILink, TColor } from '../../../core/models';
import { getCookie, setCookie } from '../../../core/service';

const BASE_URL = `${process.env.REACT_APP_API_URL}`;
const ERROR_MSG = `This AI coach is currently experiencing technical difficulties. Please try again in a few minutes. If the issue persists, contact [support@thnk.org](mailto:support@thnk.org).`;

interface IProps {
  ai: IOpenAI;
}

interface IMessage {
  id: string;
  text: string;
  role: 'user' | 'assistant';
  created_at: string;
}

interface ICookie {
  id: string;
  label: string;
}

export interface IOpenAI {
  title?: string;
  assistantID?: string;
  btnLabel?: string;
  btnId?: string;
  initialMsg?: string;
  feedback?: {
    type?: 'external' | 'scale';
    title?: string;
    cta?: ILink;
    btnText?: string;
    btnColor?: TColor;
  };
}

function Chat({ ai }: IProps) {
  const { isMobile } = useScreenSizeContext();
  const input = useFormInput('', (value) => !isEmpty(value));
  const [threads, setThreads] = useState<ICookie[]>([]);
  const [thread, setThread] = useState<string | null>(null);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [showThreads, setShowThreads] = useState(false);
  const chatMessagesRef = useRef<HTMLDivElement | null>(null);
  const { setAlert } = useAlert();

  const printError = () => {
    setChatLoading(false);
    setThreadLoading(false);
    setMessages((prevState) => [
      {
        id: Date.now().toString(),
        text: ERROR_MSG,
        role: 'assistant',
        created_at: new Date().toLocaleString(),
      },
      ...prevState,
    ]);
  };

  const updateLabel = useCallback(
    (label: string, id: string) => {
      const cookieThreads = getCookie(`openAIThread-${ai.assistantID}`);
      if (!isJsonString(cookieThreads)) return;

      const cookieThreadsArr: ICookie[] = JSON.parse(cookieThreads);
      const newCookieThreadsArr: ICookie[] = cookieThreadsArr.map((el) =>
        el.id === id ? { ...el, label } : el,
      );
      setCookie(`openAIThread-${ai.assistantID}`, JSON.stringify(newCookieThreadsArr));
      setThreads(newCookieThreadsArr);
    },
    [ai.assistantID],
  );

  const printMessages = useCallback(
    async (threadId: string) => {
      try {
        const res = await fetch(`${BASE_URL}/ai_chat/open_ai/get-messages?threadId=${threadId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const resJson = await res.json();
        const newMessages = resJson?.data?.messages?.data;

        setThreadLoading(false);
        if (newMessages.at(-1)?.role === 'user') newMessages.pop();
        setMessages(() =>
          newMessages.map((message: any) => ({
            id: message.id,
            text: message.content[0].text.value,
            role: message.role,
            created_at: new Date(message.created_at * 1000).toLocaleString(),
          })),
        );
        updateLabel(newMessages[0]?.content[0]?.text.value || 'New thread', threadId);
      } catch (error) {
        printError();
      }
    },
    [updateLabel],
  );

  const printMessageChunk = useCallback(
    (event: any) => {
      switch (event.event) {
        case 'thread.message.created':
          setMessages((prevState) => [
            {
              id: event.data.id,
              text: '',
              role: 'assistant',
              created_at: new Date(event.data.created_at * 1000).toLocaleString(),
            },
            ...prevState,
          ]);
          break;
        case 'thread.message.delta':
          setMessages((prevState) => {
            const newMessages = prevState.map((message) => ({ ...message }));
            const msgIdx = newMessages.findIndex((message) => message.id === event.data.id);
            if (msgIdx === -1) return newMessages;
            if (!newMessages[msgIdx]?.text) newMessages[msgIdx].text = '';
            newMessages[msgIdx].text += event.data.delta.content[0]?.text.value || '';
            return newMessages;
          });
          break;
        case 'thread.message.completed':
          updateLabel(event.data.content[0].text.value, event.data.thread_id);
          break;
        default:
          break;
      }
    },
    [updateLabel],
  );

  useEffect(() => {
    if (!thread) return;
    printMessages(thread);
  }, [thread, printMessages]);

  const submit = useCallback(
    async (message?: string, threadId?: string) => {
      if ((!thread && !threadId) || !ai.assistantID || chatLoading) return;
      if (isMobile) {
        input.ref?.current?.blur();
      }
      setChatLoading(true);
      if (chatMessagesRef?.current) {
        chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }
      try {
        if (!message) {
          setMessages((prevState) => [
            {
              id: new Date().toLocaleString(),
              text: input.value,
              role: 'user',
              created_at: new Date().toLocaleString(),
            },
            ...prevState,
          ]);
        }

        input.setValue('');
        const res = await fetch(`${BASE_URL}/ai_chat/open_ai/stream-message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            threadId: threadId || thread,
            assistantID: ai.assistantID,
            message: message || input.value,
          }),
        });
        const reader = res.body?.getReader();
        if (!reader) return;
        const decoder = new TextDecoder();
        let buffer: any = '';

        while (true) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const { done, value } = await reader.read();

            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop();

            for (const line of lines) {
              if (line.trim()) {
                const parsed = JSON.parse(line);
                printMessageChunk(parsed);
              }
            }

            if (buffer.trim()) {
              const parsed = JSON.parse(buffer);
              if (parsed.error || parsed.statusCode === 500) {
                throw new Error();
              }
            }
          } catch (err) {
            throw new Error();
          }
        }
        setChatLoading(false);
      } catch (err) {
        printError();
      }
    },
    [ai.assistantID, printMessageChunk, thread, isMobile, chatLoading, input],
  );

  const createNewThread = useCallback(
    async (prevThreads: { id: string; label: string }[]) => {
      setThreadLoading(true);
      setChatLoading(true);
      setMessages([]);
      try {
        const res = await fetch(`${BASE_URL}/ai_chat/open_ai/create-thread`, {
          method: 'POST',
        });
        const resJson = await res.json();
        const newThread = resJson.data.thread;
        const newThreadObj = { id: newThread.id, label: 'New thread' };
        setCookie(`openAIThread-${ai.assistantID}`, JSON.stringify([...prevThreads, newThreadObj]));
        setThreads(() => [...prevThreads, newThreadObj]);
        setThread(newThread.id);
        setThreadLoading(false);
        if (isMobile) {
          setShowThreads(false);
        }
        if (!ai.initialMsg) return;
        submit(ai.initialMsg, newThread.id);
      } catch (error) {
        printError();
      }
    },
    [ai.assistantID, ai.initialMsg, isMobile, submit],
  );

  useEffect(() => {
    const cookieThreads = getCookie(`openAIThread-${ai.assistantID}`);
    if (!isJsonString(cookieThreads)) {
      createNewThread([]);
    } else {
      const cookieThreadsArr: ICookie[] = JSON.parse(cookieThreads);
      if (cookieThreadsArr.length) {
        setThreads(cookieThreadsArr);
        setThread(cookieThreadsArr[cookieThreadsArr.length - 1].id);
      } else {
        createNewThread([]);
      }
    }
  }, []);

  const deleteThread = useCallback(
    (id: string) => {
      if (threads.length === 1) {
        setThreads([]);
        setCookie(`openAIThread-${ai.assistantID}`, JSON.stringify([]));
        setTimeout(() => {
          createNewThread([]);
        }, 0);
      } else {
        setThreads((prevState) => {
          const filteredThreads = prevState.filter((el) => el.id !== id);
          setThread(filteredThreads[0]?.id || null);
          setCookie(`openAIThread-${ai.assistantID}`, JSON.stringify(filteredThreads));
          return filteredThreads;
        });
      }
    },
    [ai.assistantID, createNewThread, threads.length],
  );

  const copyChat = useCallback(() => {
    const messagesString = [...messages]
      .reverse()
      .map((message) => `${capitalize(message.role)}: ${message.text}`)
      .join('\n');
    navigator.clipboard.writeText(messagesString);
    setAlert({ message: 'Chat copied to clipboard' });
  }, [messages, setAlert]);

  const handleKeyDown = useCallback(
    (e: any) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (isMobile) {
          input.setValue((prev) => `${prev}\n`);
        } else if (e.shiftKey) {
          input.setValue((prev) => `${prev}\n`);
        } else if (input.isValid) {
          submit();
        }
      }
    },
    [input, isMobile, submit],
  );

  return (
    <div className={`chat${threadLoading ? ' loading' : ''}`}>
      {!ai.assistantID ? null : (
        <div className="chat__container">
          <div className={`chat__content${showThreads ? ' open' : ''}`}>
            <div className="chat__chat">
              <div className="chat__header">
                {!ai.title ? null : <h2 className="h4">{ai.title}</h2>}
                <CustomButton
                  className="chat__thread-toggle btn btn--ghost"
                  type="button"
                  onClick={() => setShowThreads(!showThreads)}
                >
                  <Icon.Chat />
                  Chats ({threads.length})
                  <span className="sr-only">{showThreads ? 'hide threads' : 'show threads'}</span>
                </CustomButton>
              </div>
              <div className="chat__messages" ref={chatMessagesRef}>
                {messages?.map((message) => (
                  <div
                    className={`chat__message-container${
                      message.role === 'user' ? ' chat__message-container--user' : ''
                    }`}
                    key={message.id}
                  >
                    <Markdown className="chat__message body-regular markdown">
                      {message.text}
                    </Markdown>
                    {/* <span className="chat__message-date">{message.created_at}</span> */}
                  </div>
                ))}
              </div>
              <CustomForm className="chat__form" inputs={[input]} onSubmit={submit}>
                <div className="chat__input-container">
                  <CustomTextarea
                    className="chat__input"
                    input={input}
                    id={`chat-${ai.assistantID}`}
                    name="chat"
                    label="Type message"
                    onKeyDown={handleKeyDown}
                  />
                  <CustomButton
                    className="chat__control"
                    type="button"
                    title="Copy chat"
                    onClick={copyChat}
                  >
                    <Icon.Copy />
                    <span className="sr-only">Copy chat</span>
                  </CustomButton>
                  <CustomButton
                    className="chat__btn chat-btn"
                    type="submit"
                    disabled={chatLoading || threadLoading}
                  >
                    {chatLoading ? <Spinner /> : <Icon.Arrow />}
                  </CustomButton>
                </div>
              </CustomForm>
            </div>
            <div className="chat__threads">
              {!isMobile ? null : (
                <CustomButton
                  className="chat__thread-back btn btn--ghost"
                  type="button"
                  onClick={() => setShowThreads(false)}
                  disabled={!showThreads}
                >
                  <Icon.Arrow className="flip-x" />
                  Back to chat
                </CustomButton>
              )}
              <div className="chat__threads-title body-small">Recent chats</div>
              {threads.map((el) => (
                <div className="chat__thread-container" key={el.id}>
                  <CustomButton
                    className={`chat__thread${el.id === thread ? ' active' : ''}`}
                    type="button"
                    onClick={() => {
                      if (isMobile) {
                        setShowThreads(false);
                      }
                      if (el.id === thread) return;
                      setThreadLoading(true);
                      setThread(el.id);
                    }}
                    disabled={!showThreads || chatLoading}
                  >
                    <Markdown className="truncate markdown l-1">{el.label}</Markdown>
                  </CustomButton>
                  <CustomButton
                    className="chat__thread-delete"
                    type="button"
                    onClick={() => deleteThread(el.id)}
                    disabled={!showThreads}
                  >
                    <Icon.Bin />
                    <span className="sr-only">delete thread {el.label}</span>
                  </CustomButton>
                </div>
              ))}
              <CustomButton
                className="chat__new btn btn--dark"
                type="button"
                onClick={() => createNewThread(threads)}
                disabled={!showThreads || threadLoading || chatLoading}
              >
                New chat
                <Icon.Plus />
              </CustomButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Chat;
